import React from "react";
import Head from "next/head";
import Link from "next/link";
import Navigation from "@/layout/navigation";

export default function ForCompaniesPage() {
  return (
    <>
      <Head>
        <title>
          Featcal for Clinics | Weight-loss follow-up at scale
        </title>
        <meta
          name="description"
          content="Featcal helps weight-loss clinics and metabolic-health teams run structured follow-up between visits with patient tracking, AI-assisted food-log review, adherence nudges, and clinician-ready summaries."
        />
      </Head>

      <header className="bg-gradient-to-b from-white from-20% to-blue-300 flex flex-col gap-16 items-center justify-between overflow-hidden px-8 xl:px-0 z-[51] relative pb-20">
        <Navigation />
        <div className="flex flex-col md:items-center md:justify-center gap-6 mt-2 z-10 md:text-center">
          <p className="text-sm font-semibold text-blue-700 bg-white/70 border border-blue-200 px-4 py-2 rounded-full w-fit md:mx-auto">
            For clinics & medical centers
          </p>
          <h1 className="text-4xl md:text-5xl font-medium leading-tight max-w-3xl">
            Weight-loss follow-up your whole team can run-consistently.
          </h1>
          <p className="text-md max-w-2xl mx-auto text-slate-700">
            Featcal turns between-visit follow-up into a repeatable operating
            system: standardized tracking, automated adherence support, and
            review-ready summaries for clinicians-without adding admin burden.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:justify-center">
            <Link
              href="/start"
              className="bg-blue-600 text-white py-3 px-8 rounded-full font-semibold hover:bg-blue-700 transition-all duration-200 text-center"
            >
              Request a demo
            </Link>
            <Link
              href="/#faq"
              className="bg-white text-slate-900 py-3 px-8 rounded-full font-semibold border border-blue-200 hover:bg-blue-50 transition-all duration-200 text-center"
            >
              See FAQs
            </Link>
          </div>
        </div>
      </header>

      <main id="main-content" className="bg-white">
        <section className="py-16 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto bg-white border border-slate-200 rounded-3xl p-8">
            <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-3">
              The problem: programs break between appointments
            </h2>
            <p className="text-slate-700 leading-relaxed">
              Most centers rely on a patchwork of apps, spreadsheets, and
              messages. Patients track inconsistently, staff spends time chasing
              data, and clinicians don’t get a clear picture until the next
              visit. Featcal fixes the between-visit gap with a structured
              program experience that patients actually follow-whether the goal
              is weight loss, weight gain, or metabolic health.
            </p>
          </div>
        </section>

        <section className="py-20 px-8 xl:px-0 pt-0">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-3 gap-8">
            {[
              {
                title: "Standardize the pathway",
                desc: "Make the patient journey consistent across clinicians and cohorts: the same tracking, the same review flow, and a predictable follow-up rhythm.",
              },
              {
                title: "Improve adherence between visits",
                desc: "Patients change between visits. Daily visibility, reminders, and clear next steps keep momentum without extra calls and manual follow-ups.",
              },
              {
                title: "Reduce operational load",
                desc: "Reduce manual chasing with cleaner summaries, proactive nudges, and fewer scattered messages-so teams focus on care, not coordination.",
              },
            ].map((card) => (
              <div
                key={card.title}
                className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-3xl p-8 shadow-xl shadow-blue-100/30"
              >
                <h2 className="text-xl font-semibold text-slate-900 mb-3">
                  {card.title}
                </h2>
                <p className="text-slate-700 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="py-20 pt-0 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-start">
            <div className="bg-white border border-slate-200 rounded-3xl p-8">
              <h2 className="text-3xl md:text-4xl font-medium text-slate-900 mb-4">
                Built for teams and programs (not just a single practitioner)
              </h2>
              <p className="text-slate-700 leading-relaxed mb-6">
                Weight loss centers need more than tracking. You need a program
                that scales: consistent patient experience, consistent data
                collection, and consistent review-across your whole team.
              </p>
              <ul className="space-y-3 text-slate-700">
                {[
                  "Configure tracking by pathway (weight, meals, activity, sleep, hydration, etc.).",
                  "Review trend views and weekly summaries in minutes.",
                  "Automate reminders to reduce drop-offs and missed appointments.",
                  "Keep plans, goals, and next steps always accessible to patients.",
                ].map((p) => (
                  <li key={p} className="flex gap-3">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-3xl p-8 shadow-xl shadow-blue-100/30">
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">
                Typical program scenarios
              </h3>
              <ul className="space-y-3 text-slate-700">
                {[
                  "Medical weight loss programs combining visits + structured between-visit follow-up.",
                  "Obesity pathways coordinated by nurses, dietitians, and clinicians.",
                  "Metabolic health / prediabetes programs focused on adherence and behavior change.",
                  "Post-intervention monitoring after medication changes or procedures.",
                ].map((p) => (
                  <li key={p} className="flex gap-3">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="bg-gradient-to-b from-white to-blue-50 py-20 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-medium text-slate-900 mb-4">
              A program experience patients actually follow
            </h2>
            <p className="text-slate-700 max-w-3xl mb-12">
              Featcal is a simple PWA for patients and a review system for
              teams. It drives daily actions, reduces friction, and keeps
              everyone aligned-so your pathway is executed consistently.
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              {[
                {
                  title: "Daily patient tracking",
                  points: [
                    "Weight, meals, activity, sleep, hydration, workouts",
                    "Clear targets and progress visibility",
                    "Less reliance on paper forms and scattered apps",
                  ],
                },
                {
                  title: "Structured plans & resources",
                  points: [
                    "Plans delivered in-app (always up to date)",
                    "Recipes and guidance to reduce confusion",
                    "Automated reminders to support adherence",
                  ],
                },
                {
                  title: "Smarter program review",
                  points: [
                    "Trend views that highlight adherence gaps and risk signals",
                    "Faster preparation before appointments",
                    "Less manual data cleaning and copy/paste",
                  ],
                },
                {
                  title: "Operational wins",
                  points: [
                    "Fewer no-shows via reminders",
                    "Less inbound noise for coordinators",
                    "More consistent patient journeys at scale",
                  ],
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="bg-white border border-blue-100 rounded-3xl p-8 shadow-xl shadow-blue-100/20"
                >
                  <h3 className="text-xl font-semibold text-slate-900 mb-4">
                    {item.title}
                  </h3>
                  <ul className="space-y-2 text-slate-700">
                    {item.points.map((p) => (
                      <li key={p} className="flex gap-3">
                        <span className="text-green-600 font-bold">✓</span>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="mt-12 flex flex-col sm:flex-row gap-3">
              <Link
                href="/start"
                className="bg-blue-600 text-white py-3 px-8 rounded-full font-semibold hover:bg-blue-700 transition-all duration-200 text-center"
              >
                Request a demo
              </Link>
              <Link
                href="/"
                className="bg-white text-slate-900 py-3 px-8 rounded-full font-semibold border border-blue-200 hover:bg-blue-50 transition-all duration-200 text-center"
              >
                Back to home
              </Link>
            </div>
          </div>
        </section>

        <section className="py-20 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-medium text-slate-900 mb-4">
                Implementation that won’t slow your team down
              </h2>
              <p className="text-slate-700 leading-relaxed mb-8">
                You don’t need a multi-month project to improve follow-up.
                Featcal is designed to onboard quickly and adapt to your
                existing workflow.
              </p>
              <div className="space-y-4">
                {[
                  {
                    title: "1) Define your pathway",
                    desc: "Choose what patients track and how often, aligned with your program and team roles.",
                  },
                  {
                    title: "2) Launch with a cohort",
                    desc: "Start with a pilot group to validate cadence, messaging, and the review workflow.",
                  },
                  {
                    title: "3) Scale confidently",
                    desc: "Standardize templates and iterate once-then roll out across cohorts and sites.",
                  },
                  {
                    title: "4) Improve continuously",
                    desc: "Use adherence signals and trend views to intervene earlier and strengthen outcomes over time.",
                  },
                ].map((s) => (
                  <div
                    key={s.title}
                    className="bg-white border border-slate-200 rounded-2xl p-6"
                  >
                    <p className="font-semibold text-slate-900">{s.title}</p>
                    <p className="text-slate-700 mt-1">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-3xl p-8 shadow-xl shadow-blue-100/30">
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">
                What teams typically improve
              </h3>
              <ul className="space-y-3 text-slate-700">
                {[
                  "Higher adherence between visits (more consistent tracking and plan execution).",
                  "Less coordinator time spent chasing and organizing data.",
                  "More structured consultations with clearer trends and context.",
                  "A consistent program experience across the organization.",
                ].map((p) => (
                  <li key={p} className="flex gap-3">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link
                  href="/start"
                  className="bg-blue-600 text-white py-3 px-8 rounded-full font-semibold hover:bg-blue-700 transition-all duration-200 text-center"
                >
                  Request a demo
                </Link>
                <Link
                  href="/privacy-policy"
                  className="bg-white text-slate-900 py-3 px-8 rounded-full font-semibold border border-blue-200 hover:bg-blue-50 transition-all duration-200 text-center"
                >
                  Privacy & security
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
